/**
 * The app's OWN words, in the reader's language (plan.md §54 Phase 2: "EN/HE
 * with RTL").
 *
 * Two translation mechanisms exist and they are deliberately separate:
 *
 *  - **Content** — act names, blurbs, info pages — is translated by the
 *    FESTIVAL, as data, through `useLocale().t(base, translations)`.
 *  - **Chrome** — "Sign in", "My lineup", "Nothing on this day" — is
 *    translated HERE, by us, as code, through `useT()`.
 *
 * ## Keyed by the English, on purpose
 *
 * `tr('Sign in')`, not `tr('auth.signIn')`. The English text IS the key, so a
 * screen reads the same as before, a missing Hebrew entry falls back to the
 * English a reviewer can see, and there is no second file of made-up key names
 * to keep in step with the first.
 *
 * Variables are `{name}` placeholders: `tr('{count} acts', { count })`. The
 * whole sentence is the key, because word order differs between languages —
 * never build a sentence by concatenating translated fragments.
 *
 * ## Nothing half-translated ships
 *
 * `i18n.test.ts` reads every `tr('…')` literal in the source and fails when
 * one has no Hebrew entry. `UI_LANGUAGES` (`@myrave/shared`) offers
 * Hebrew only because that test passes — the 2026-09-14 lesson was that a
 * right-to-left page of English words is worse than an English page.
 *
 * The Hebrew lives in one file per area (`he/visitor.ts`, `he/app.ts`,
 * `he/admin.ts`, `he/ui.ts`) so the areas can be translated independently.
 */
import { useCallback } from 'react';
import { languageOf, type Locale } from '@myrave/shared';
import { useLocale } from '@/app/LocaleProvider';
export type Vars = Readonly<Record<string, string | number>>;

import { dictionaryFor, type Entry } from './dictionaries';

export { dictionaryReady, loadDictionary } from './dictionaries';

function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    (name in vars ? String(vars[name]) : whole));
}

/**
 * The entry's text for these vars. A plural entry picks its form by the
 * language's own rules (`Intl.PluralRules`) from `vars.count` — "{count}
 * days" is "יומיים" at 2 in Hebrew, and Arabic has six forms. A missing form
 * falls back to `other`.
 */
function pick(entry: Entry, locale: Locale, vars?: Vars): string {
  if (typeof entry === 'string') return entry;
  const count = Number(vars?.count);
  if (!Number.isFinite(count)) return entry.other;
  let rule: Intl.LDMLPluralRule = 'other';
  try { rule = new Intl.PluralRules(languageOf(locale)).select(count); } catch { /* other */ }
  return entry[rule] ?? entry.other;
}

/** Translate outside React (tests, module-level helpers given a locale). */
export function translate(locale: Locale, english: string, vars?: Vars): string {
  const entry = dictionaryFor(locale)?.[english];
  return fill(entry === undefined ? shownEnglish(english) : pick(entry, locale, vars), vars);
}

/**
 * A key may carry a CONTEXT after `‖` — `'Find a rave‖menu'` — when one English sentence needs two
 * translations in a language (the user, 2026-10-26: the Hebrew menu row says "חזרה לרשימת הרייבים",
 * the page "מציאת רייב"). The context is never shown: English reads the words before it.
 */
export function shownEnglish(key: string): string {
  const at = key.indexOf('‖');
  return at === -1 ? key : key.slice(0, at);
}

/**
 * `const tr = useT(); tr('Sign in')`.
 *
 * Call `tr` with a STRING LITERAL (or a literal plus vars). The coverage test
 * finds keys by reading the source; a key built at runtime is one it cannot
 * see, and therefore one that can ship untranslated.
 */
export function useT(): (english: string, vars?: Vars) => string {
  const { locale } = useLocale();
  return useCallback((english: string, vars?: Vars) => translate(locale, english, vars), [locale]);
}
